import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';
import type { AdminRole } from '@/lib/api-admin';

/**
 * QA-514 역할별 접근 메뉴(user-service, 전달 문서 BO-TO-BE-2026-10-23-ops-admin-products-role-menus).
 * GET /admin/v1/role-menus → [{ role, menus: string[] | null, updatedAt }] (null = 제한 없음),
 * PUT /admin/v1/role-menus/{role} { menus: string[] | null }.
 * BE 반영 전이면 404/405 — 화면은 "서버 반영 후" 로 안내한다.
 */
export interface RoleMenus {
  role: AdminRole;
  menus: string[] | null;
  updatedAt: string | null;
}

export class RoleMenusNotReadyError extends Error {}

const BASE = '/admin/v1/role-menus';
export const roleMenusQueryKey = ['admin', 'role-menus'] as const;

export const useRoleMenusQuery = (enabled: boolean) =>
  useQuery<RoleMenus[]>({
    queryKey: roleMenusQueryKey,
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await adminFetch(BASE);
      if (res.status === 404 || res.status === 405) throw new RoleMenusNotReadyError('not ready');
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: RoleMenus[] | null };
      return env.data ?? [];
    },
  });

export const useSaveRoleMenusMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ role, menus }: { role: AdminRole; menus: string[] | null }) => {
      const path = `${BASE}/${encodeURIComponent(role)}`;
      const body = { menus };
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (res.status === 404 || res.status === 405) throw new RoleMenusNotReadyError('not ready');
      if (!res.ok) throw await errorFromResponse(res);
      // 같은 설정으로 되돌려 저장하는 경우가 첫 응답 재생으로 묻히지 않게 키를 푼다.
      releaseIdempotencyKey('PUT', path, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: roleMenusQueryKey }),
  });
};
